
import React, { useState, useRef } from "react";
import { SendHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

interface ChatInputProps {
  onSend: (message: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  disabled?: boolean;
  isMobile?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  isLoading = false,
  placeholder = "Skriv din melding her...",
  disabled = false,
  isMobile = false
}) => {
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !isLoading) {
      onSend(message);
      setMessage("");
      
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    
    if (textareaRef.current) {
      textareaRef.current.style.height = "0px";
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = isMobile ? 120 : 150;
      textareaRef.current.style.height = Math.min(scrollHeight, maxHeight) + "px";
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={`w-full border-t ${isMobile ? 'p-3' : 'p-2'} flex items-end gap-2 bg-background`}
    >
      <Textarea
        ref={textareaRef}
        value={message}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`${isMobile ? 'min-h-12 max-h-32 text-base' : 'min-h-10 max-h-40 text-base'} resize-none border rounded-md flex-1 py-3 px-3 touch-manipulation`}
        disabled={isLoading || disabled}
        style={{
          fontSize: isMobile ? '16px' : '14px', // Prevents zoom on iOS
        }}
      />
      <Button
        type="submit"
        size="icon"
        variant="ghost"
        className={`${isMobile ? 'h-12 w-12' : 'h-10 w-10'} rounded-full flex-shrink-0 touch-manipulation`}
        disabled={!message.trim() || isLoading || disabled}
      >
        <SendHorizontal className={`${isMobile ? 'h-6 w-6' : 'h-5 w-5'} ${isLoading ? "opacity-50" : ""}`} />
      </Button>
    </form>
  );
};

export default ChatInput;
